import type {Column} from './types';

type CsvCell = string | number | boolean | null | undefined;
type CsvRow = Record<string, CsvCell>;

/** Returns true if at least one column has CSV export configured. */
export function hasExportableColumns<T>(columns: Column<T>[]): boolean {
  return columns.some((c) => c.csv);
}

/** Build header → value map for each row, skipping columns without `csv` config. */
export function buildCsvRows<T>(data: T[], columns: Column<T>[]): CsvRow[] {
  const csvCols = columns.filter((c) => c.csv);
  return data.map((item) => {
    const row: CsvRow = {};
    for (const col of csvCols) {
      const header = col.csv!.header ?? col.id;
      row[header] = col.csv!.value(item);
    }
    return row;
  });
}

/** Escape a single CSV cell. Quotes fields containing comma, quote, newline, or carriage return. */
function escapeCsvCell(value: CsvCell): string {
  if (value === null || value === undefined) return '';
  const s = typeof value === 'string' ? value : String(value);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

/** Serialize rows to a CSV string. Header row uses keys from the first row. */
export function rowsToCsvString(rows: CsvRow[]): string {
  if (rows.length === 0) return '';
  const headers = Object.keys(rows[0]);
  const lines: string[] = [];
  lines.push(headers.map(escapeCsvCell).join(','));
  for (const row of rows) {
    lines.push(headers.map((h) => escapeCsvCell(row[h])).join(','));
  }
  return lines.join('\r\n');
}
